import diagnosticsApi from '@/api/operations/diagnosticsApi'
import { SyncErrorIcon } from '@/components/icons'
import { useAuthContext } from '@/features/auth/context'
import {
  DiagnosticRow,
  ErrorStatusCell,
  MUTED_TEXT,
  ReasonText,
  SuggestedActionList,
  groupedCellSx,
  primaryActionSx,
  secondaryActionSx,
} from '@/features/config/components/modal/NotificationModal/ErrorGuidance'
import useStationErrorLogs from '@/features/config/hooks/station/useStationErrorLogs'
import useStationResetById from '@/features/config/hooks/station/useStationResetById'
import { NotifyService } from '@/helpers/notify'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import { getStationErrorDetailText } from '@/utils/stationErrorDetail'
import { stationErrorLogKey } from '@/utils/stationErrorLog'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { FC, Fragment, useEffect, useState } from 'react'

interface SoilTableProps {
  rows: any
}

interface DiagnosedDetail {
  /** Diagnose found the station working: the error code is from the past. */
  passed: boolean
  description: string
  reason: string
  actions: string[]
  /** Only set for a Diagnose result: the code it reported and when. */
  code?: number
  at?: number
}

const LOCAL_KEY_ERROR = 'id-error'

const headCellSx = {
  fontSize: '12px',
  fontWeight: 600,
  color: MUTED_TEXT,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.4px',
  whiteSpace: 'nowrap' as const,
}

const SoilPopupTable: FC<SoilTableProps> = ({ rows }) => {
  const { t } = useTranslation()

  const { mutate: resetById, isSuccess } = useStationResetById()
  const { interfaceId } = useAuthContext()
  const { isViewOnly } = usePermission()
  const { data: errorLogs, isInitialLoading: errorLogsLoading } =
    useStationErrorLogs(Number(interfaceId), rows)

  const [arrayReset, setArrayReset] = useState<any>([])
  const [diagnoseLoading, setDiagnoseLoading] = useState<number[]>([])
  const [diagnosedById, setDiagnosed] = useState<
    Record<number, DiagnosedDetail>
  >({})
  const getKeyReset = localStorage.getItem(LOCAL_KEY_ERROR)

  useEffect(() => {
    const getKeyReset = localStorage.getItem(LOCAL_KEY_ERROR)
    if (getKeyReset) {
      setArrayReset(JSON.parse(getKeyReset))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Guidance for the recorded error, from the latest io::on / io::off log
  const getLoggedDetail = (row: any) =>
    getStationErrorDetailText(
      t,
      row.errorCode,
      errorLogs?.get(stationErrorLogKey(row.address, row.index, row.errorCode))
        ?.errDetail,
    )

  const handleResetId = (id: any) => {
    if (isViewOnly) return

    if (getKeyReset === null || !getKeyReset?.includes(id)) {
      resetById({
        interfaceId: interfaceId,
        id: id,
      })
      const arrayNew = [...arrayReset, id]
      setArrayReset(arrayNew)
      if (!isSuccess) {
        setTimeout(function () {
          setArrayReset([])
        }, 2000)
      }
    }
  }

  const handleDiagnose = async (id: number) => {
    setDiagnoseLoading((prev) => (prev.includes(id) ? prev : [...prev, id]))
    try {
      const res = await diagnosticsApi.runDiagnostic({
        interfaceId: Number(interfaceId),
        lastStationId: 0,
        stationIds: [id],
      })
      const data = res.data
      if (Array.isArray(data) && data.length > 0) {
        const item = data[0]
        const error = item?.error
        setDiagnosed((prev) => ({
          ...prev,
          [id]: {
            passed: item?.status ? item.status === 'Normal' : !error,
            code: error?.code,
            at: Date.now(),
            description: error?.description ?? '',
            reason: error?.reason ?? '',
            actions: String(error?.action ?? '').split('\n'),
          },
        }))
      }
    } catch (error) {
      console.error('Station diagnose failed:', error)
      NotifyService.error(error)
    } finally {
      setDiagnoseLoading((prev) => prev.filter((x) => x !== id))
    }
  }

  return (
    <Stack flexGrow="1">
      <TableContainer component={Paper} elevation={0}>
        <Table sx={{ width: '100%' }} aria-label={t('stationError')}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, pl: '20px' }}>
                {t('adminStationNameLabel')}
              </TableCell>
              <TableCell sx={headCellSx}>{t('commonStatusError')}</TableCell>
              <TableCell sx={headCellSx}>{t('diagnosticReason')}</TableCell>
              <TableCell sx={headCellSx}>{t('suggestedAction')}</TableCell>
              <TableCell sx={{ ...headCellSx, textAlign: 'right', pr: '20px' }}>
                {t('interfaceTableHeaderAction')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ '& .MuiTableCell-root': { verticalAlign: 'top' } }}>
            {rows.map((row: any, rowIndex: number) => {
              const logged = getLoggedDetail(row)
              const diagnosed = diagnosedById[row.id]
              const isDiagnosing = diagnoseLoading.includes(row.id)
              const hasDiagnosticRow = isDiagnosing || !!diagnosed?.at
              const past = !!diagnosed?.passed
              const rowBg = rowIndex % 2 ? '#F9F9F9' : '#FFFFFF'
              const cellSx = {
                py: 2,
                ...(hasDiagnosticRow ? groupedCellSx : {}),
              }
              const loggedAt = errorLogs?.get(
                stationErrorLogKey(row.address, row.index, row.errorCode),
              )?.timestamp
              return (
                <Fragment key={row.id}>
                  <TableRow sx={{ bgcolor: rowBg }}>
                    <TableCell sx={{ ...cellSx, pl: '20px', width: 170 }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                        {row.controller || '-'}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: MUTED_TEXT,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('formsLabelsDecoder')}{' '}
                        {`${row.address}-${row.index}`}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, width: 190 }}>
                      <ErrorStatusCell
                        code={row.errorCode}
                        message={row.errorName}
                        recordedAt={loggedAt}
                        recordedAtLoading={errorLogsLoading}
                        past={past}
                      />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <ReasonText text={logged?.reason} muted={past} />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <SuggestedActionList
                        actions={logged?.actions ?? []}
                        muted={past}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, pr: '20px', width: 150 }}>
                      <Stack
                        spacing={1}
                        alignItems="stretch"
                        sx={{ opacity: isViewOnly ? 0.5 : 1 }}
                      >
                        <LoadingButton
                          variant="primary"
                          loading={isDiagnosing}
                          disabled={isViewOnly}
                          onClick={() => handleDiagnose(row.id)}
                          sx={primaryActionSx}
                        >
                          {diagnosed
                            ? t('errorSummaryDiagnoseAgain')
                            : t('errors.diagnose')}
                        </LoadingButton>
                        <LoadingButton
                          variant="secondary"
                          loading={arrayReset?.includes(row.id)}
                          loadingPosition="start"
                          startIcon={<SyncErrorIcon />}
                          disabled={isViewOnly}
                          onClick={() => handleResetId(row.id)}
                          sx={secondaryActionSx}
                        >
                          {t('errors.acknowledge')}
                        </LoadingButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <DiagnosticRow
                    running={isDiagnosing}
                    rowBg={rowBg}
                    loggedReason={logged?.reason}
                    loggedActions={logged?.actions}
                    recordedCode={row.errorCode}
                    diagnosis={
                      diagnosed?.at
                        ? {
                            passed: diagnosed.passed,
                            code: diagnosed.code,
                            message: diagnosed.description,
                            reason: diagnosed.reason,
                            actions: diagnosed.actions,
                            at: diagnosed.at,
                          }
                        : undefined
                    }
                  />
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default SoilPopupTable
